import { ComputeFunction } from '@/types/compute';
import { getBackendImageServer } from '@/utils/getBackendImageServer';
import { getImageServer } from '@/utils/getImageServer';
import { getUrl, isValidUrl } from '@/utils/getUrl';
import { slugify } from '@/utils/slugify';
import breakpoints from 'base/configs/grid.breakpoints';
import { isNotNull, isNotUndefined } from 'typesafe-utils';

type GenerateImageSrcProps = {
  filename: string;
  imageData: any;
  isPreview: boolean;
  maxWidth: number;
  minWidth: number;
  compression?: number;
};

export const generateImageSrc = ({
  filename,
  imageData,
  isPreview,
  maxWidth,
  minWidth,
  compression,
}: GenerateImageSrcProps) => {
  const whRatio = imageData.whRatio || imageData.defaultAspectRatio || 1.916;
  const bbRatio = imageData.bbRatio || 1;
  const resolvedMaxWidth = (maxWidth || minWidth) * bbRatio;
  const resolvedMinWidth = minWidth * bbRatio;
  const resolvedOriginalWidth = imageData.originalWidth * bbRatio || resolvedMaxWidth;

  const width = Math.max(resolvedMinWidth, Math.min(resolvedMaxWidth, resolvedOriginalWidth));
  const height = width / whRatio;

  const imageParams: Record<string, string> = {
    imageId: String(imageData.imageId),
    width: String(Math.round(width)),
    height: String(Math.round(height)),
    format: 'webp',
    compression: compression ? String(compression) : '90',
  };

  const imageParameters = [
    'x',
    'y',
    'cropw',
    'croph',
    'panox',
    'panoy',
    'panow',
    'panoh',
    'heightx',
    'heighty',
    'heightw',
    'heighth',
  ];

  Object.keys(imageData).forEach((key) => {
    const value = imageData[key];
    const isValidParameter = imageParameters.includes(key);
    const isValidValue = isNotNull(value) && isNotUndefined(value);

    if (isValidParameter && isValidValue) {
      imageParams[key] = parseFloat(value.toFixed(2)).toString();
    }
  });

  const pathname = filename.split('?')[0];
  const base = isPreview ? getBackendImageServer() : getImageServer();
  const url = getUrl(pathname, base);

  if (!url) return null;

  url.search = new URLSearchParams(imageParams).toString();

  return url;
};

export const labradorImage: ComputeFunction = async ({ contentData, pageData, device }) => {
  const { data, meta } = contentData ?? {};
  const { desktop, mobile } = data.deviceImageData ?? {};

  meta.device = device;

  // Compatibility
  desktop.cropw ??= data.cropw;
  desktop.croph ??= data.croph;
  desktop.x ??= data.x;
  desktop.y ??= data.y;
  desktop.bbRatio ??= data.bbRatio;
  desktop.whRatio ??= data.whRatio;

  desktop.panox ??= data.panox;
  desktop.panoy ??= data.panoy;
  desktop.panoh ??= data.panoh;
  desktop.panow ??= data.panow;

  desktop.heightx ??= data.heightx;
  desktop.heighty ??= data.heighty;
  desktop.heighth ??= data.heighth;
  desktop.heightw ??= data.heightw;

  desktop.float ??= data.float ?? 'floatNone';

  data.imageId ??= data.instanceOfId || 0;
  delete data.instanceOfId;

  data.caption = data.imageCaption || '';
  delete data.imageCaption;

  data.byline = data.imageByline || '';
  delete data.imageByline;

  if (!data.imageId) {
    return contentData;
  }

  // If an image doesn't have a whRatio value we need to use the dafault one
  const defaultAspectRatio = 1.916;

  // Viewport max widths
  const maxPageWidth = breakpoints.xl; // TODO: Get from some kind of setting

  // Data that is common for both desktop and mobile
  const baseData = {
    imageId: data.imageId,
    originalWidth: data.originalWidth,
    defaultAspectRatio,
  };

  data.url = isValidUrl(data.url) ? data.url : null;

  // Because image data is set on the viewport it is added they become the default values for all
  // viewports unless specifically overwritten. We merge them together, each viewport taking
  // priority with its own data.
  const deviceData = {
    desktop: {
      ...mobile,
      ...desktop,
      ...baseData,
    },
    mobile: {
      ...desktop,
      ...mobile,
      ...baseData,
    },
  };

  const filename = slugify(data.seoName) || `image-${data.imageId}`;

  // If we're loading a preview we need to use a different image server
  const isPreview = Boolean(pageData.meta.isPreview);

  // Generate the actual URLs. We can adjust the minWidth and maxWidth here but because next/image
  // does an excellent job of generating srcsets we only need the original size.
  const desktopSrcUrl = generateImageSrc({
    filename,
    imageData: deviceData.desktop,
    maxWidth: maxPageWidth * (meta.pageWidth.desktop / 100),
    minWidth: maxPageWidth * 1 * (meta.pageWidth.desktop / 100),
    isPreview,
  });

  const mobileSrcUrl = generateImageSrc({
    filename,
    imageData: deviceData.mobile,
    maxWidth: maxPageWidth * (meta.pageWidth.mobile / 100),
    minWidth: maxPageWidth * 1 * (meta.pageWidth.mobile / 100),
    isPreview,
  });

  // Pass the URLs along to the component
  data.imageUrl = {
    desktop: desktopSrcUrl?.toString() ?? data.url,
    mobile: mobileSrcUrl?.toString() ?? data.url,
  };

  // Image alignment
  data.align = {
    desktop: desktop.align || 'none',
    mobile: mobile.align || 'none',
  };

  // No need to send this stuff to the frontend - save on them kb's!
  delete data.x;
  delete data.y;
  delete data.heightx;
  delete data.heighty;
  delete data.heightw;
  delete data.heighth;
  delete data.cropw;
  delete data.croph;
  delete data.deviceImageData;
  delete data.width;
  delete data.height;
  delete data.whRatio;

  return contentData;
};

export default labradorImage;
